import {Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ScheduleHelper} from './helper/schedule-helper.enum';
import {ExtractHelper} from './helper/extract-helper.enum';
import {UserSavedRequestService} from '../../../../../../../services/apiServices/business/user-saved-request.service';
import {EmailValidator} from '../../../../../../../model/email-validator';
import {CustomDate} from '../../../../../../../model/custom-date';
import {FileSaverService} from 'ngx-filesaver';
import {CommonService} from '../../../../../../../services/apiServices/business/common.service';

/**
 * Handle custom request scheduling
 */
@Component({
  selector: 'app-scheduling',
  templateUrl: './scheduling.component.html',
  styleUrls: ['./scheduling.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SchedulingComponent implements OnInit, OnChanges {

  /**
   * Output Binding for modal opening
   */
  @Output() public displayComponentChange: EventEmitter<boolean>;

  /**
   * Input Binding for modal opening
   */
  @Input('displayComponent') displayComponent: boolean;

  /**
   * Boolean, to extend parameters to display once a frequency is selected
   */
  @Input( 'isSelectedFrequency') isSelectedFrequency: boolean;

  /**
   * the currently active request gave by parent component
   */
  @Input('activeRequest') activeRequest: any;

  @ViewChild( 'frequency_options', { static: false } ) frequency_options: any;

  /**
   * List of scheduling options (build by component)
   */
  public schedulingOptions: any[];

  /**
   * Selected Scheduling option
   */
  public selectedSchedulingOption: string;

  /**
   * List of extraction options (build by component)
   */
  public extractionOptions: any[];

  /**
   * List of selected extraction options
   */
  public selectedExtractionOption: any[];

  /**
   * List of columns for export
   */
  public columnsList: any[];

  /**
   * List of selected columns for export
   */
  public selectedColumns: any[];

  /**
   * List of e-mail seized by user
   */
  public emailList: string[];

  /**
   * Batch configuration
   */
  public batchContent: string;

  /**
   * Temporary email usefull to add emails (before validation)
   */
  public newEmail: string;

  /**
   * Label for Daily Period
   */
  private dailyPeriodLabel: string;

  /**
   * Label for Weekly Period
   */
  private weeklyPeriodLabel: string;

  /**
   * Label for Monthly Period
   */
  private monthlyPeriodLabel: string;

  /**
   * Label for Monthly J+4 Period
   */
  private monthlyJ4PeriodLabel: string;

  /**
   * Label for quarterly Period
   */
  private quarterlyPeriodLabel: string;

  /**
   * Label for inactivity option (from extractionOptions)
   */
  private inactivityReportingLabel: string;

  /**
   * Label for batch option (from extractionOptions)
   */
  private batchReportingLabel: string;

  /**
   * Not modified list of columns gave by back-office
   */
  private columnsSaved: any[];

  /**
   * Title of the message, display when the save request is ok
   */
  private successTitle: string;

  /**
   * Title of the message, display when the save request is ko
   */
  private errorTitle: string;

  /**
   * Message, display when the save request is ok
   */
  private successMessage: string;

  /**
   * Message, display when the save request is ko
   */
  private errorMessage: string;
  /* message for executing saved request*/
  private successDownloadTitle: string;
  private successDownloadMessage: string;
  private emptyDownloadTitle: string;
  private emptyDownloadMessage: string;
  /**
   * Instantiate variables
   * @param {TranslateService} translate - handle component translation
   * @param {UserSavedRequestService} userSavedRequestService - handle back office request (save)
   * @param fileSaverService
   * @param commonService
   */
  constructor(private translate: TranslateService, private userSavedRequestService: UserSavedRequestService,
              private fileSaverService: FileSaverService,
              private commonService: CommonService) {
    this.displayComponentChange = new EventEmitter<boolean>();
    this.selectedColumns = [];
    this.columnsList = [];
    this.selectedExtractionOption = [];
  }

  /**
   * Turn on isSelectedFrequency to display related parameters
   */
  public hideScheduleRequestModal( value ): void {
    this.displayComponentChange.emit( value );
    this.resetAllChoices();
    this.isSelectedFrequency = false;
  }

  public setIsSelectedFrequency( event ): void {
    if ( event.option.value === this.selectedSchedulingOption ) {
      this.isSelectedFrequency = false;
      this.selectedSchedulingOption = null;
      this.frequency_options.value = null;
    } else {
      this.selectedSchedulingOption = event.option.value;
      this.isSelectedFrequency = true;
    }
  }

  /**
   * Init component variables values and build lists
   */
  public ngOnInit(): void {
    this.isSelectedFrequency = false;

    this.resetAllChoices();
    this.getTradForComponent();
    this.buildColumnsList();
    this.buildSchedulingOptions();
    this.buildExtractionOptions();
  }

  /**
   * Reset every choice to default values
   */
  private resetAllChoices(): void {
    this.selectedSchedulingOption = null;
    this.selectedColumns = [];
    this.batchContent = '';
    this.newEmail = '';
    this.emailList = [];
    this.selectedExtractionOption = [];
  }

  /**
   * On active request change: reset to default and try to retrieve all informations available
   */
  public ngOnChanges(): void {
    this.resetAllChoices();
    this.retrieveIfExistFilters();
    this.reorderList();
  }

  /**
   * try to retrieve all available info for request scheduling
   */
  private retrieveIfExistFilters(): void {
    if (this.activeRequest.scheduleFilters) {
      // Retrieving Schedule option
      if (this.activeRequest.scheduleFilters.sExecuteScheduling) {
        this.isSelectedFrequency = true;
        this.selectedSchedulingOption = this.activeRequest.scheduleFilters.sExecuteScheduling;
      }

      // Retrieving Emails
      if (this.activeRequest.scheduleFilters.sMail) {
        this.retrieveMailFromactiveRequest(this.activeRequest.scheduleFilters.sMail);
      }

      // Retrieving batch content
      if (this.activeRequest.scheduleFilters.sParamBatch) {
        this.batchContent = this.activeRequest.scheduleFilters.sParamBatch;
      }

      // Retrieving Data To extract : activity
      if (this.activeRequest.scheduleFilters.bMailNoActivity) {
        this.selectedExtractionOption.push(ExtractHelper.INACTIVITY_REPORT);
      }

      // Retrieving Data To extract : bBatch
      if (this.activeRequest.scheduleFilters.bBatch) {
        this.selectedExtractionOption.push(ExtractHelper.BACTH);
      }

      // Retrieving selected columns
      if (this.activeRequest.scheduleFilters.listDataSentRef) {
        for (const selectedColumn of this.activeRequest.scheduleFilters.listDataSentRef) {
          if (selectedColumn.bSelected) {
            this.selectedColumns.push(selectedColumn.sNameId);
          }
        }
      }
    }
  }

  /**
   * Parse the list of email gave by back and handle correct format translation
   * @param {string} notParsed - back-office formatted list of email
   */
  private retrieveMailFromactiveRequest(notParsed: string): void {
    this.emailList = notParsed.split(';').filter(x => x.length !== 0);
  }

  /**
   * Build the list of selected columns
   */
  private buildColumnsList(): void {
    this.userSavedRequestService.getColumnsForPlanification().subscribe(columns => {
      this.columnsSaved = columns;
      for (const column of columns) {
        const token = 'LABEL.GIVEN.BY.BACK.global.dataTracking.' + column.sNameId;

        this.translate.get( token ).subscribe((translation: string) => {
          this.columnsList.push({label: translation, value: column.sNameId });
        });
      }
    });
  }

  /**
   * Build the column list (static, not dynamic)
   */
  private buildSchedulingOptions(): void {
    this.schedulingOptions = [];
    this.schedulingOptions.push({label: this.dailyPeriodLabel, value: ScheduleHelper.DAILY});
    this.schedulingOptions.push({label: this.weeklyPeriodLabel, value: ScheduleHelper.WEEKLY});
    this.schedulingOptions.push({label: this.monthlyPeriodLabel, value: ScheduleHelper.MONTHLY});
    this.schedulingOptions.push({label: this.monthlyJ4PeriodLabel, value: ScheduleHelper.MONTHLYJ4});
    this.schedulingOptions.push({label: this.quarterlyPeriodLabel, value: ScheduleHelper.QUATERLY});
  }

  /**
   * Build the extraction options (static, not dynamic)
   */
  private buildExtractionOptions(): void {
    this.extractionOptions = [];
    this.extractionOptions.push({label: this.inactivityReportingLabel, value: ExtractHelper.INACTIVITY_REPORT});
    this.extractionOptions.push({label: this.batchReportingLabel, value: ExtractHelper.BACTH});
  }

  /**
   * Determine if the batch option is selected
   * @return {boolean} - true if it's true
   */
  public batchisSelected(): boolean {
    if (this.selectedExtractionOption.length > 0) {
      for (const option of this.selectedExtractionOption) {
        if (option === ExtractHelper.BACTH) {
          return true;
        }
      }
      return false;
    } else {
      return false;
    }
  }

  /**
   * Add a new email inside EmailList and reset newEmail value
   */
  public addNewEmail(): void {
    this.emailList.push(this.newEmail);
    this.newEmail = '';
  }

  /**
   * Determine if the whole form is valid
   */
  public formIsValid(): boolean {
    if ( this.selectedColumns.length > 0 ) {
      if ( this.selectedSchedulingOption && this.emailList.length > 0 && this.batchContentIsValid() ) {
        return true;
      } else  if ( this.selectedSchedulingOption ) {
        return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Determine if the batch content is valid
   * @return {boolean} - true if it's valid
   */
  private batchContentIsValid(): boolean {
    return this.batchisSelected() ? this.batchContent.length > 0 : true;
  }

  /**
   * Remove an email from the emailList
   * @param {string} email - the email that should be removed
   */
  public removeEmail(email: string): void {
    this.emailList = this.emailList.filter(x => x !== email);
  }

  /**
   * Determine if the new email typed by user is valid
   * @return {boolean} - true if it's valid
   */
  public newEmailIsValid(): boolean {
    return EmailValidator.emailIsValid(this.newEmail) && (this.emailList.indexOf(this.newEmail) < 0);
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('TOASTER.SUCCESS_MESSAGE_TITLE').subscribe((title: string) => {
      this.successDownloadTitle = title;
    });
    this.translate.get('TOASTER.SUCCESS_DOWNLOAD_OK').subscribe((title: string) => {
      this.successDownloadMessage = title;
    });
    this.translate.get('TOASTER.WARNING_MESSAGE_TITLE').subscribe((title: string) => {
      this.emptyDownloadTitle = title;
    });
    this.translate.get('TOASTER.REQUEST_NO_RESULT').subscribe((title: string) => {
      this.emptyDownloadMessage = title;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.PERIODICITY.DAILY').subscribe((translation: string) => {
      this.dailyPeriodLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.PERIODICITY.WEEKLY').subscribe((translation: string) => {
      this.weeklyPeriodLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.PERIODICITY.MONTHLY').subscribe((translation: string) => {
      this.monthlyPeriodLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.PERIODICITY.MONTHLYJ4').subscribe((translation: string) => {
      this.monthlyJ4PeriodLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.PERIODICITY.QUARTERLY').subscribe((translation: string) => {
      this.quarterlyPeriodLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.EXTRACTION.INACTIVITY').subscribe((translation: string) => {
      this.inactivityReportingLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.ADD.OR.UPDATE.SAVED.REQUEST_COMPONENT.SCHEDULING_COMPONENT.EXTRACTION.BATCH').subscribe((translation: string) => {
      this.batchReportingLabel = translation;
    });
    this.translate.get('TOASTER.SUCCESS_MESSAGE_TITLE').subscribe((translation: string) => {
      this.successTitle = translation;
    });
    this.translate.get('TOASTER.ERROR_MESSAGE_TITLE').subscribe((translation: string) => {
      this.errorTitle = translation;
    });
    this.translate.get('TOASTER.SAVE_REQUEST_SUCCESS_MESSAGE').subscribe((translation: string) => {
      this.successMessage = translation;
    });
    this.translate.get('TOASTER.SAVE_REQUEST_ERROR_MESSAGE').subscribe((translation: string) => {
      this.errorMessage = translation;
    });
  }
  updateFilterByFrequency() {
    this.activeRequest.simpleFilters.bDepotDay = (this.selectedSchedulingOption === ScheduleHelper.DAILY);
    this.activeRequest.simpleFilters.bDepotMonth  = (this.selectedSchedulingOption === ScheduleHelper.MONTHLY || this.selectedSchedulingOption === ScheduleHelper.MONTHLYJ4 );
    this.activeRequest.simpleFilters.bDepotWeek = (this.selectedSchedulingOption === ScheduleHelper.DAILY);
  }
  /**
   * save the scheduled request
   */
  public save(): void {
    this.updateFilterByFrequency();
    const bMailNoActivity = this.selectedExtractionOption.indexOf(ExtractHelper.INACTIVITY_REPORT) >= 0;
    const bBatch = this.selectedExtractionOption.indexOf(ExtractHelper.BACTH) >= 0;
    this.userSavedRequestService.scheduleRequest(
      this.activeRequest.requestName, this.getEmailListAsString(), this.batchContent, this.selectedSchedulingOption,
      bMailNoActivity, bBatch, this.activeRequest.simpleFilters, this.formatColumns()
    ).subscribe(res => {
      if (res) {
        this.commonService.addToast({life: 3000, severity: 'success', summary: this.successTitle, detail: this.successMessage});
        this.displayComponentChange.emit(false);
      } else {
        this.commonService.addToast({life: 3000, severity: 'error', summary: this.errorTitle, detail: this.errorMessage});
      }

    });
  }
  /**
   * execute the scheduled request
   */
  public execute(): void {

    const bMailNoActivity = this.selectedExtractionOption.indexOf(ExtractHelper.INACTIVITY_REPORT) >= 0;
    const bBatch = this.selectedExtractionOption.indexOf(ExtractHelper.BACTH) >= 0;
    this.userSavedRequestService.executeSavedRequest(
      this.activeRequest.requestName, this.getEmailListAsString(), this.batchContent, this.selectedSchedulingOption,
      bMailNoActivity, bBatch, this.activeRequest.simpleFilters, this.formatColumns()
    ).subscribe(response => {
      if (response.body && response.body.size && response.body.size > 0) {
        const blob = new Blob([ response.body ], {type: response.headers.get('content-type')});
        this.fileSaverService.save(blob, 'export_' + CustomDate.getFormatedToday() + '.csv');
        this.commonService.addToast({
          life: 3000,
          severity: 'success',
          summary: this.successDownloadTitle,
          detail: this.successDownloadMessage
        });
      }
      else {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: this.emptyDownloadTitle,
          detail: this.emptyDownloadMessage
        });
      }

    });
  }
  /**
   * format emailList as a string for back office request
   * @return {string} - the result like a@b.fr;t@b.fr
   */
  private getEmailListAsString(): string {
    let result = '';
    for (let icpt = 0; icpt < this.emailList.length; icpt++) {
      result += icpt === this.emailList.length - 1 ? this.emailList[icpt] : this.emailList[icpt] + ';';
    }
    return result;
  }

  /**
   * put selected value for all columns for back office request
   *
   * @return {any[]} - list of modified columns
   */
  private formatColumns(): any[] {
    const result = [];

    for (const column of this.columnsSaved) {
      result.push(column);
      result[result.length - 1].bSelected = this.selectedColumns.indexOf(column.sNameId) >= 0;
    }
    return result;
  }


  reorderList() {
    if (this.selectedColumns && this.selectedColumns.length > 0) {
      const temp  = [];
      for (const col of this.columnsList) {
        if (this.selectedColumns.indexOf(col.value) >= 0) {
          temp.push(col);
        }
      }
      for (const col of this.columnsList) {
        if (this.selectedColumns.indexOf(col.value) < 0) {
          temp.push(col);
        }
      }
      this.columnsList = temp;
    }
  }
}
